import { SITE_ORIGIN, absoluteOgImageUrl, DEFAULT_OG_IMAGE_PATH } from './constants.js'
import { blogPostsMeta } from '../data/blogPostsMeta.js'

/**
 * @typedef {{ title: string, description: string, path: string, image?: string, imageAlt?: string, twitterCard?: string }} SeoPayload
 */

const DEFAULT_IMAGE = absoluteOgImageUrl(DEFAULT_OG_IMAGE_PATH)

const DEFAULT_IMAGE_ALT =
  'Akara merchant dashboard showing deposit requests, statuses, and workflow for property and rental teams in the UAE.'

/** @type {SeoPayload} */
const HOME = {
  title: 'Security Deposit Management Software for UAE Businesses | Akara',
  description:
    'Security deposit management software for UAE rental and service businesses. Send requests, hold cards without charging, release or claim with a full audit trail.',
  path: '/',
}

/**
 * Ordered: first match wins. Use specific paths before prefixes.
 * @type {Array<{ test: (path: string) => boolean, seo: Omit<SeoPayload, 'image' | 'imageAlt'> & Partial<Pick<SeoPayload, 'image' | 'imageAlt'>> }>}
 */
const ROUTES = [
  {
    test: (p) => p === '/',
    seo: HOME,
  },
  {
    test: (p) => p === '/waitlist',
    seo: {
      title: 'Join the waitlist | Akara',
      description:
        'Get early access to Akara — security deposit management for UAE rental and service businesses. Tell us about your deposits and secure your spot.',
      path: '/waitlist',
    },
  },
  {
    test: (p) => p === '/solutions/property-managers',
    seo: {
      title: 'Property managers & real estate – Security deposits | Akara',
      description:
        'Tenant deposit management for UAE property teams — automate requests from your PMS or channel manager, reduce disputes, and release holds instantly.',
      path: '/solutions/property-managers',
    },
  },
  {
    test: (p) => p === '/solutions/healthcare',
    seo: {
      title: 'Healthcare – Patient deposits & appointments | Akara',
      description:
        'Patient deposit collection for UAE clinics — secure appointment deposits, cut no-shows and overdue balances, and keep patients informed.',
      path: '/solutions/healthcare',
    },
  },
  {
    test: (p) => p === '/solutions/vehicle-rental',
    seo: {
      title: 'Car Rental Deposit Software – Deposit-Free Checkouts | Akara',
      description:
        'Car rental deposit software for UAE fleets — offer deposit-free checkouts with card holds, automated releases, and protection against damage and fines.',
      path: '/solutions/vehicle-rental',
    },
  },
  {
    test: (p) => p === '/solutions/equipment-rental',
    seo: {
      title: 'Equipment rental – Digital deposits | Akara',
      description:
        'Equipment rental deposit management — digitize deposits, cut administrative overhead, and protect your gear with branded guest flows.',
      path: '/solutions/equipment-rental',
    },
  },
  {
    test: (p) => p === '/deposit-free',
    seo: {
      title: 'Rent Without a Deposit in Dubai – Deposit-Free Renting | Akara',
      description:
        'Deposit-free renting in Dubai explained: what "no deposit" really means, how renters find it, and how businesses offer deposit-free rentals with a card hold instead of cash.',
      path: '/deposit-free',
    },
  },
  {
    test: (p) => p === '/how-card-holds-work',
    seo: {
      title: 'How Card Holds Work: Pre-Authorization Payments in the UAE | Akara',
      description:
        'How a card hold (pre-authorization) works: money reserved on a card without being charged. Hold vs charge, how long holds last, and why they are the modern security deposit.',
      path: '/how-card-holds-work',
    },
  },
  {
    test: (p) => p === '/docs',
    seo: {
      title: 'Akara API & Integrations Documentation',
      description:
        'Connect Akara to your PMS. API reference, quickstart, webhooks, and PMS/channel-manager integration guides.',
      path: '/docs',
    },
  },
  {
    test: (p) => p === '/docs/api',
    seo: {
      title: 'API Reference | Akara Docs',
      description:
        'Inbound and outbound webhooks for deposits, claims, and integrations. Endpoints, auth, payloads, and signature verification for developers.',
      path: '/docs/api',
    },
  },
  {
    test: (p) => p === '/support',
    seo: {
      title: 'Support | Akara',
      description:
        'Get help with Akara deposits, payouts, disputes, and account settings. Contact our team in the UAE.',
      path: '/support',
    },
  },
  {
    test: (p) => p === '/contact-us',
    seo: {
      title: 'Contact Us | Akara',
      description:
        'Get in touch with Akara for onboarding, support, and product questions in the United Arab Emirates.',
      path: '/contact-us',
    },
  },
  {
    test: (p) => p === '/pricing',
    seo: {
      title: 'Pricing – Per-deposit plans | Akara',
      description:
        'Transparent per-deposit pricing with flexible packages, predictable costs, and support that scales with your volume.',
      path: '/pricing',
    },
  },
  {
    test: (p) => p === '/privacy-policy',
    seo: {
      title: 'Privacy policy | Akara',
      description: 'How Akara collects, uses, and protects personal data for merchants and guests.',
      path: '/privacy-policy',
    },
  },
  {
    test: (p) => p === '/terms',
    seo: {
      title: 'Terms of service | Akara',
      description: 'Terms governing use of the Akara security deposit platform for businesses and users.',
      path: '/terms',
    },
  },
  {
    test: (p) => p === '/faq',
    seo: {
      title: 'FAQ – Security deposits | Akara',
      description:
        'Answers about deposits, releases, disputes, integrations, and pricing for property and rental businesses.',
      path: '/faq',
    },
  },
  {
    test: (p) => p === '/blog',
    seo: {
      title: 'Blog – Security deposit insights | Akara',
      description:
        'Guides and comparisons on security deposits for UAE rental and property businesses — from Ejari basics to deposit-free car rental.',
      path: '/blog',
    },
  },
  {
    test: (p) => p === '/get-started',
    seo: {
      title: 'Get started – Create your Akara account',
      description:
        'Register your business, choose your package, and start sending secure deposit requests in minutes.',
      path: '/get-started',
    },
  },
  {
    test: (p) => p === '/login',
    seo: {
      title: 'Login | Akara',
      description: 'Sign in to your Akara merchant dashboard to manage deposits, claims, and settings.',
      path: '/login',
    },
  },
  {
    test: (p) => p === '/reset-password',
    seo: {
      title: 'Reset password | Akara',
      description: 'Set a new password for your Akara merchant account.',
      path: '/reset-password',
    },
  },
  {
    test: (p) => p.startsWith('/deposit/'),
    seo: {
      title: 'Your security deposit | Akara',
      description:
        'View and complete your deposit securely. Akara protects guests and businesses across the UAE.',
      path: '/deposit', // canonical path pattern; og:url uses full path in applyDocumentSeo
    },
  },
  {
    test: (p) => p === '/dashboard' || p === '/dashboard/',
    seo: {
      title: 'Dashboard | Akara',
      description: 'Manage deposit requests, claims, reports, and integrations in your Akara workspace.',
      path: '/dashboard',
    },
  },
  {
    test: (p) => p.startsWith('/dashboard/requests'),
    seo: {
      title: 'Deposit requests | Akara',
      description: 'Track and manage outgoing deposit requests, statuses, and guest actions.',
      path: '/dashboard/requests',
    },
  },
  {
    test: (p) => p.startsWith('/dashboard/claims'),
    seo: {
      title: 'Claims | Akara',
      description: 'Review and resolve deposit claims with documentation and workflow tools.',
      path: '/dashboard/claims',
    },
  },
  {
    test: (p) => p.startsWith('/dashboard/reports'),
    seo: {
      title: 'Reports | Akara',
      description: 'Analytics and reporting for deposits and operations.',
      path: '/dashboard/reports',
    },
  },
  {
    test: (p) => p.startsWith('/dashboard/integrations'),
    seo: {
      title: 'Integrations | Akara',
      description: 'Connect PMS, channels, and tools to automate deposit workflows.',
      path: '/dashboard/integrations',
    },
  },
  {
    test: (p) => p.startsWith('/dashboard/webhooks'),
    seo: {
      title: 'Webhooks | Akara',
      description: 'Configure outbound webhooks for deposit and claim events.',
      path: '/dashboard/webhooks',
    },
  },
  {
    test: (p) => p.startsWith('/dashboard/settings'),
    seo: {
      title: 'Settings | Akara',
      description: 'Company profile, team, notifications, and security preferences.',
      path: '/dashboard/settings',
    },
  },
]

function normalizePathname(pathname) {
  if (!pathname || pathname === '/') return '/'
  return pathname.endsWith('/') ? pathname.slice(0, -1) || '/' : pathname
}

/**
 * @param {string} pathname - `location.pathname`
 * @returns {SeoPayload}
 */
export function getSeoForPath(pathname) {
  const path = normalizePathname(pathname)

  if (path.startsWith('/blog/')) {
    const slug = path.slice('/blog/'.length)
    const meta = blogPostsMeta.find((m) => m.slug === slug)
    if (meta) {
      return {
        // seoTitle is an optional shorter <title> for posts whose display H1
        // (meta.title) would push the tag past Semrush's ~75-char limit.
        title: `${meta.seoTitle ?? meta.title} | Akara`,
        description: meta.description,
        path,
        image: DEFAULT_IMAGE,
        imageAlt: DEFAULT_IMAGE_ALT,
        twitterCard: 'summary_large_image',
      }
    }
  }

  for (const { test, seo } of ROUTES) {
    if (test(path)) {
      const base = {
        ...seo,
        image: seo.image ?? DEFAULT_IMAGE,
        imageAlt: seo.imageAlt ?? DEFAULT_IMAGE_ALT,
        twitterCard: seo.twitterCard ?? 'summary_large_image',
      }
      if (path.startsWith('/deposit/')) {
        return {
          ...base,
          path,
          title: base.title,
          description: base.description,
        }
      }
      return base
    }
  }

  return {
    title: HOME.title,
    description: HOME.description,
    path,
    image: DEFAULT_IMAGE,
    imageAlt: DEFAULT_IMAGE_ALT,
    twitterCard: 'summary_large_image',
  }
}

/**
 * Full canonical URL for the current route (no hash).
 * Trailing slash to match the URL Netlify actually serves for the prerendered
 * directory files (/pricing -> 301 -> /pricing/); a slash-less canonical would
 * point at the redirecting URL, not the 200.
 * @param {string} pathname
 */
export function canonicalUrlForPath(pathname) {
  const path = normalizePathname(pathname)
  if (path === '/') return `${SITE_ORIGIN}/`
  return `${SITE_ORIGIN}${path}/`
}
